import { User, X, LogOut, ChevronDown, ChevronRight, FileText, Activity, ShieldAlert, Monitor, Cpu, Users, Server, Settings } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { getUser, apiFetch, clearAuth } from '../api/client';

interface HeaderProps {
  onNavigateDashboard?: () => void;
  activeView?: string;
  onViewChange?: (view: string) => void;
  isAdmin?: boolean;
}

export function Header({ onNavigateDashboard, activeView, onViewChange, isAdmin }: HeaderProps) {
  const authUser = getUser();
  const [showSettings, setShowSettings] = useState(false);
  const [userName] = useState(authUser?.name || 'John Doe');
  const [userRole] = useState(authUser?.role === 'admin' ? 'Administrator' : 'Client');

  const [manageOpen, setManageOpen] = useState(false);
  const [machinesOpen, setMachinesOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const manageRef = useRef<HTMLDivElement>(null);
  const machinesTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const devicesTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (manageRef.current && !manageRef.current.contains(e.target as Node)) {
        setManageOpen(false);
        setDevicesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigate = (view: string) => {
    onViewChange?.(view);
    setManageOpen(false);
    setMachinesOpen(false);
    setDevicesOpen(false);
  };

  const handleMachinesEnter = () => {
    if (machinesTimeout.current) clearTimeout(machinesTimeout.current);
    setMachinesOpen(true);
    setDevicesOpen(false);
  };

  const handleMachinesLeave = () => {
    machinesTimeout.current = setTimeout(() => setMachinesOpen(false), 150);
  };

  const handleDevicesEnter = () => {
    if (devicesTimeout.current) clearTimeout(devicesTimeout.current);
    setDevicesOpen(true);
    setMachinesOpen(false);
  };

  const handleDevicesLeave = () => {
    devicesTimeout.current = setTimeout(() => setDevicesOpen(false), 150);
  };

  const handleCancelSettings = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwError('');
    setPwSuccess('');
    setShowSettings(false);
  };

  const handleChangePassword = async () => {
    setPwError('');
    setPwSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwError('All password fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    setPwLoading(true);
    try {
      await apiFetch('/auth/me/password', {
        method: 'PATCH',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      setPwSuccess('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      setPwError(err instanceof Error ? err.message : 'Failed to update password.');
    } finally {
      setPwLoading(false);
    }
  };

  const dropdownItem = (icon: React.ReactNode, label: string, view: string) => (
    <button
      key={view}
      onClick={() => navigate(view)}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors rounded-md ${
        activeView === view
          ? 'bg-[#00D4BE]/10 text-[#00D4BE]'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">

          {/* Logo — navigates to dashboard */}
          <button
            onClick={() => onNavigateDashboard?.()}
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-none p-0"
          >
            <svg width="35" height="28" viewBox="0 0 28 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="3" cy="14" r="2.5" stroke="#00D4BE" strokeWidth="1.5"/>
              <circle cx="10" cy="10" r="2.5" stroke="#00D4BE" strokeWidth="1.5"/>
              <circle cx="18" cy="13" r="2.5" stroke="#00D4BE" strokeWidth="1.5"/>
              <circle cx="25" cy="4" r="2.5" stroke="#00D4BE" strokeWidth="1.5"/>
              <line x1="5.2" y1="12.7" x2="7.8" y2="11.3" stroke="#00D4BE" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="12.2" y1="10.8" x2="15.8" y2="12.2" stroke="#00D4BE" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="20.2" y1="11.7" x2="22.8" y2="6.3" stroke="#00D4BE" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '1.375rem', letterSpacing: '0.12em', color: '#00D4BE', textTransform: 'uppercase' }}>
              PerfectCircle
            </span>
          </button>

          {/* Centre nav */}
          <div className="flex items-center gap-2" ref={manageRef}>
            <div className="relative">
              <button
                onClick={() => { setManageOpen(!manageOpen); setDevicesOpen(false); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  manageOpen ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                Manage
                <ChevronDown className={`w-4 h-4 transition-transform ${manageOpen ? 'rotate-180' : ''}`} />
              </button>

              {manageOpen && (
                <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 z-50">

                  {/* Available to all users */}
                  {dropdownItem(<FileText className="w-4 h-4" />, 'Reports', 'reports')}
                  {dropdownItem(<Activity className="w-4 h-4" />, 'Anomaly Indicators', 'anomaly-indicators')}
                  {dropdownItem(<Settings className="w-4 h-4" />, 'Impact Score', 'bearings')}

                  {/* Admin-only items */}
                  {isAdmin && (
                    <>
                      {dropdownItem(<ShieldAlert className="w-4 h-4" />, 'Alarm Manager', 'alarm-manager')}

                      <div className="my-1.5 border-t border-gray-100" />

                      {/* Machines flyout */}
                      <div
                        className="relative"
                        onMouseEnter={handleMachinesEnter}
                        onMouseLeave={handleMachinesLeave}
                      >
                        <button className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                          <div className="flex items-center gap-3">
                            <Monitor className="w-4 h-4" />
                            Machines
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>

                        {machinesOpen && (
                          <div
                            className="absolute left-full top-0 ml-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 z-50"
                            onMouseEnter={handleMachinesEnter}
                            onMouseLeave={handleMachinesLeave}
                          >
                            <button
                              onClick={() => navigate('machines-add')}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              Add Machine
                            </button>
                            <button
                              onClick={() => navigate('machines-copy')}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              Copy Machine
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Devices flyout */}
                      <div
                        className="relative"
                        onMouseEnter={handleDevicesEnter}
                        onMouseLeave={handleDevicesLeave}
                      >
                        <button className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                          <div className="flex items-center gap-3">
                            <Cpu className="w-4 h-4" />
                            Devices
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>

                        {devicesOpen && (
                          <div
                            className="absolute left-full top-0 ml-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 z-50"
                            onMouseEnter={handleDevicesEnter}
                            onMouseLeave={handleDevicesLeave}
                          >
                            <button
                              onClick={() => navigate('devices-add')}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              Add Device
                            </button>
                            <button
                              onClick={() => navigate('devices-review')}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              Review Devices
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="my-1.5 border-t border-gray-100" />

                      {dropdownItem(<Server className="w-4 h-4" />, 'Databases', 'databases')}
                      {dropdownItem(<Users className="w-4 h-4" />, 'User Management', 'users')}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* User account */}
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-3 pl-4 border-l border-gray-200 hover:bg-gray-50 rounded-lg px-3 py-2 transition-all cursor-pointer"
          >
            <div className="text-right">
              <p className="text-sm text-gray-900">{userName}</p>
              <p className="text-xs text-gray-500">{userRole}</p>
            </div>
            <div className="w-9 h-9 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-gray-600" />
            </div>
          </button>
        </div>
      </header>

      {/* Account Settings Modal */}
      {showSettings && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-30 z-50" onClick={handleCancelSettings} />
          <div
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-gray-900">Account Settings</h2>
              <button onClick={handleCancelSettings} className="p-1 hover:bg-gray-100 rounded transition-colors">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="px-6 py-6 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-8 h-8 text-gray-600" />
                </div>
                <div>
                  <p className="text-gray-900 font-medium">{userName}</p>
                  <p className="text-gray-500 text-sm">{userRole}</p>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-gray-700 font-medium border-b border-gray-100 pb-2">Change Password</h3>
                <div>
                  <label htmlFor="current-pw" className="block text-gray-700 mb-1 text-sm">Current Password</label>
                  <input id="current-pw" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter current password" />
                </div>
                <div>
                  <label htmlFor="new-pw" className="block text-gray-700 mb-1 text-sm">New Password</label>
                  <input id="new-pw" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="At least 8 characters" />
                </div>
                <div>
                  <label htmlFor="confirm-pw" className="block text-gray-700 mb-1 text-sm">Confirm New Password</label>
                  <input id="confirm-pw" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Repeat new password" />
                </div>
                {pwError && <p className="text-red-600 text-sm">{pwError}</p>}
                {pwSuccess && <p className="text-green-600 text-sm">{pwSuccess}</p>}
                <button onClick={handleChangePassword} disabled={pwLoading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60">
                  {pwLoading ? 'Updating…' : 'Update Password'}
                </button>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
              <button onClick={() => { clearAuth(); window.location.reload(); }}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm">
                <LogOut className="w-4 h-4" />
                Log Out
              </button>
              <button onClick={handleCancelSettings}
                className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
